import { buildHeadersWithWidgetToken } from '@elfsight/lib-utils';

type TranslateResponse = {
  phrases: string[];
  language: Record<'source' | 'target', string>;
};

const TRANSLATE_ENDPOINT = 'https://phrase-translator.utils.elfsightcdn.com/';

const MAX_CHARS_PER_REQUEST = 5000;

export type RequestParams = {
  sourceLanguage?: string;
  targetLanguage?: string;
  // @deprecated
  language?: string;
  widgetToken?: string;
  signal?: AbortSignal;
};

function chunkPhrasesByCharCount(phrases: string[], maxChars: number) {
  const { chunks, currentChunk } = phrases.reduce<{
    chunks: string[][];
    currentChunk: string[];
    chars: number;
  }>(
    (accumulator, phrase) => {
      const phraseLength = phrase.length;

      if (phraseLength > maxChars) {
        if (accumulator.currentChunk.length) {
          accumulator.chunks.push(accumulator.currentChunk);
          accumulator.currentChunk = [];
          accumulator.chars = 0;
        }

        accumulator.chunks.push([phrase]);
        return accumulator;
      }

      if (
        accumulator.currentChunk.length &&
        accumulator.chars + phraseLength > maxChars
      ) {
        accumulator.chunks.push(accumulator.currentChunk);
        accumulator.currentChunk = [];
        accumulator.chars = 0;
      }

      accumulator.currentChunk.push(phrase);
      accumulator.chars += phraseLength;

      return accumulator;
    },
    { chunks: [], currentChunk: [], chars: 0 }
  );

  if (currentChunk.length) {
    chunks.push(currentChunk);
  }

  return chunks;
}

async function fetchPhrasesChunk(
  phrases: string[],
  params: RequestParams
): Promise<TranslateResponse> {
  const response = await fetch(TRANSLATE_ENDPOINT, {
    method: 'POST',
    headers: buildHeadersWithWidgetToken(params.widgetToken, {
      'content-type': 'application/json'
    }),
    body: JSON.stringify({
      phrases,
      language: {
        source: params.sourceLanguage,
        target: params.language || params.targetLanguage
      }
    }),
    signal: params.signal
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response.json();
}

export async function fetchPhrases(
  phrases: string[],
  params: RequestParams
): Promise<TranslateResponse> {
  const chunks = chunkPhrasesByCharCount(phrases, MAX_CHARS_PER_REQUEST);

  if (chunks.length === 1) {
    return fetchPhrasesChunk(chunks[0], params);
  }

  const chunkResults = await Promise.all(
    chunks.map((chunk) => fetchPhrasesChunk(chunk, params))
  );

  const {
    allPhrases,
    sourceLanguage = '',
    targetLanguage = ''
  } = chunkResults.reduce<{
    allPhrases: string[];
    sourceLanguage?: string;
    targetLanguage?: string;
  }>(
    (accumulator, result) => {
      accumulator.allPhrases.push(...result.phrases);
      if (!accumulator.sourceLanguage && result.language.source) {
        accumulator.sourceLanguage = result.language.source;
      }

      if (!accumulator.targetLanguage && result.language.target) {
        accumulator.targetLanguage = result.language.target;
      }

      return accumulator;
    },
    {
      allPhrases: []
    }
  );

  return {
    phrases: allPhrases,
    language: {
      source: sourceLanguage,
      target: targetLanguage
    }
  };
}
